<template>
  <div 
    v-if="isActive" 
    class="fixed inset-0 z-[9999] flex items-start justify-center lg:justify-center px-3 sm:px-6 py-6 pointer-events-none"
  >
    <div class="pointer-events-auto relative mx-auto w-full max-w-6xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden max-h-[calc(100vh-96px)] min-h-[65vh]">
      <!-- Header -->
      <div class="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between shrink-0">
        <AiOnboardingProgressTracker />
        <button
          class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          :title="$t('GENERAL.CLOSE') || 'Đóng'"
          @click="handleClose"
        >
          <lucide-icon name="x" :size="20" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-4 sm:p-6 pb-24">
        <component
          ref="currentStep"
          :is="currentStepComponent"
          @next="handleNext"
          @back="handleBack"
          @skip="handleSkip"
          @error="handleGenerationError"
          @complete="handleComplete"
        />
      </div>

      <!-- Actions Footer - Sticky -->
      <div class="sticky bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur border-t border-slate-200 dark:border-slate-700 flex justify-between items-center p-4 sm:p-6 shrink-0 z-10">
        <woot-button 
          variant="clear"
          color-scheme="secondary"
          @click="handleBack"
        >
          <lucide-icon v-if="currentStep > 0 && currentStep < 7" name="chevron-left" :size="16" class="mr-2" />
          <template v-if="currentStep === 0">
            {{ $t('AI_ONBOARDING.DEMO_HOOK.SKIP_BTN') || 'Để sau, tôi muốn tự khám phá' }}
          </template>
          <template v-else-if="currentStep === 7">
            {{ $t('AI_ONBOARDING.ACTIVATION.SKIP_BTN') || 'Để sau, vào dashboard' }}
          </template>
          <template v-else>
            {{ $t('GENERAL.BACK') || 'Quay lại' }}
          </template>
        </woot-button>
        
        <woot-button
          variant="smooth"
          color-scheme="primary"
          :disabled="!canProceed"
          @click="triggerStepNext"
        >
          <template v-if="currentStep === 0">
            {{ $t('AI_ONBOARDING.DEMO_HOOK.START_BTN') || 'Bắt đầu setup ngay!' }}
          </template>
          <template v-else-if="currentStep === 7">
            {{ $t('AI_ONBOARDING.ACTIVATION.DASHBOARD_BTN') || 'Vào Dashboard' }}
          </template>
          <template v-else>
            {{ $t('GENERAL.CONTINUE') || 'Tiếp tục' }}
          </template>
          <lucide-icon v-if="currentStep === 0" name="arrow-right" :size="18" class="ml-2" />
          <lucide-icon v-else-if="currentStep === 7" name="arrow-right" :size="16" class="ml-2" />
          <lucide-icon v-else name="chevron-right" :size="16" class="ml-2" />
        </woot-button>
      </div>

      <!-- Error Toast -->
      <div v-if="error" class="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md sm:w-auto">
        <div class="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl shadow-lg">
          <lucide-icon name="circle-alert" :size="20" class="text-red-600 dark:text-red-400 shrink-0" />
          <span class="text-sm text-red-800 dark:text-red-200 flex-1">{{ error }}</span>
          <button
            class="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"
            @click="clearError"
          >
            <lucide-icon name="x" :size="16" class="text-red-600 dark:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import AiOnboardingProgressTracker from './components/AiOnboardingProgressTracker.vue';
import AiDemoHookStep from './components/phase1/AiDemoHookStep.vue';
import AiBotModesStep from './components/phase1/AiBotModesStep.vue';
import AiProviderSetupStep from './components/phase1/AiProviderSetupStep.vue';
import AiBotProfileStep from './components/phase1/AiBotProfileStep.vue';
import AiConversationalQuestionsStep from './components/phase1/AiConversationalQuestionsStep.vue';
import AiGenerationLoadingStep from './components/phase1/AiGenerationLoadingStep.vue';
import AiFirstChatTestStep from './components/phase1/AiFirstChatTestStep.vue';
import AiActivationStep from './components/phase1/AiActivationStep.vue';

export default {
  name: 'AiOnboardingFlowContainer',
  
  components: {
    LucideIcon,
    AiOnboardingProgressTracker,
    AiDemoHookStep,
    AiBotModesStep,
    AiProviderSetupStep,
    AiBotProfileStep,
    AiConversationalQuestionsStep,
    AiGenerationLoadingStep,
    AiFirstChatTestStep,
    AiActivationStep,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'completed'],

  data() {
    return {
      autoSaveTimer: null,
      lastSavedState: null,
      stepComponents: [
        AiDemoHookStep,
        AiBotProfileStep,
        AiBotModesStep,
        AiProviderSetupStep,
        AiConversationalQuestionsStep,
        AiGenerationLoadingStep,
        AiFirstChatTestStep,
        AiActivationStep,
      ],
    };
  },

  computed: {
    ...mapState('aiOnboarding', ['isActive', 'currentStep']),
    ...mapGetters('aiOnboarding', ['canProceedToNextStep', 'progressPercentage']),
    
    error() {
      return this.$store.state.aiOnboarding.uiFlags.error;
    },

    hasUnsavedChanges() {
      const currentState = JSON.stringify(this.$store.state.aiOnboarding);
      return this.lastSavedState !== currentState;
    },

    currentStepComponent() {
      return this.stepComponents[this.currentStep] || this.stepComponents[0];
    },

    // Check canProceed từ step component trước, fallback về store getter
    canProceed() {
      const stepComponent = this.$refs.currentStep;
      // Thử gọi method getCanProceed nếu có
      if (stepComponent && typeof stepComponent.getCanProceed === 'function') {
        return stepComponent.getCanProceed();
      }
      // Thử access computed property trực tiếp (Vue 2)
      if (stepComponent && stepComponent.$options.computed && stepComponent.$options.computed.canProceed) {
        return stepComponent.canProceed;
      }
      // Fallback về store getter nếu step component không expose canProceed
      return this.canProceedToNextStep;
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initializeFlow();
        } else {
          this.clearAutoSaveTimer();
        }
      },
    },

    '$store.state.aiOnboarding': {
      deep: true,
      handler() {
        this.scheduleAutoSave();
      },
    },
  },

  beforeUnmount() {
    this.clearAutoSaveTimer();
    if (this.hasUnsavedChanges) {
      this.persistProgress(true);
    }
  },

  methods: {
    ...mapActions('aiOnboarding', {
      initOnboardingState: 'initAiOnboarding',
      resumeProgressAction: 'resumeOnboarding',
      persistProgressAction: 'saveProgress',
      nextOnboardingStep: 'nextStep',
      previousOnboardingStep: 'previousStep',
      resetOnboardingState: 'resetOnboarding',
    }),

    async initializeFlow() {
      try {
        await this.resumeProgressAction();
        if (!this.isActive) {
          await this.initOnboardingState();
        }
        this.lastSavedState = JSON.stringify(this.$store.state.aiOnboarding);
      } catch (error) {
        console.error('Failed to init AI onboarding:', error);
        useAlert(this.$t('AI_ONBOARDING.ERRORS.INIT_FAILED') || 'Không thể khởi tạo onboarding');
      }
    },

    triggerStepNext() {
      if (this.currentStep === 7) {
        if (this.$refs.currentStep && typeof this.$refs.currentStep.goToDashboard === 'function') {
          this.$refs.currentStep.goToDashboard();
        }
        return;
      }
      // Trigger step's handleNext if it exists, otherwise proceed directly
      if (this.$refs.currentStep && typeof this.$refs.currentStep.handleNext === 'function') {
        this.$refs.currentStep.handleNext();
      } else {
        this.handleNext();
      }
    },

    handleNext() {
      if (!this.canProceedToNextStep) {
        useAlert(this.$t('AI_ONBOARDING.ERRORS.COMPLETE_REQUIRED') || 'Vui lòng hoàn thành các thông tin bắt buộc');
        return;
      }

      this.nextOnboardingStep();
      this.persistProgress(true);
    },

    handleBack() {
      if (this.currentStep === 0) {
        this.handleClose();
        return;
      }
      if (this.currentStep === 7) {
        if (this.$refs.currentStep && typeof this.$refs.currentStep.skipForNow === 'function') {
          this.$refs.currentStep.skipForNow();
        } else {
          this.handleSkip();
        }
        return;
      }
      this.previousOnboardingStep();
      this.persistProgress(true);
    },

    handleSkip() {
      this.handleClose();
    },

    async handleClose() {
      if (this.hasUnsavedChanges) {
        await this.persistProgress(true);
        useAlert(this.$t('GENERAL.SAVED') || 'Đã lưu');
      }
      this.resetOnboardingState();
      this.lastSavedState = JSON.stringify(this.$store.state.aiOnboarding);
      this.$emit('close');
    },

    handleGenerationError(error) {
      useAlert(error || this.$t('AI_ONBOARDING.ERRORS.GENERATION_FAILED') || 'Không thể tạo dữ liệu');
    },

    handleComplete() {
      this.resetOnboardingState();
      this.$emit('completed');
      useAlert(this.$t('AI_ONBOARDING.SUCCESS.COMPLETED') || '🎉 Hoàn tất thiết lập AI!');
    },

    clearError() {
      this.$store.commit('aiOnboarding/AI_ONBOARDING_SET_UI_FLAG', { error: null });
    },

    scheduleAutoSave() {
      // Clear existing timer to prevent spam
      this.clearAutoSaveTimer();
      
      // Only schedule if onboarding is active
      if (!this.show) return;
      
      // Check if state actually changed
      const currentState = JSON.stringify(this.$store.state.aiOnboarding);
      if (currentState === this.lastSavedState) return;
      
      this.autoSaveTimer = setTimeout(() => {
        this.persistProgress(true);
      }, 3000);
    },

    clearAutoSaveTimer() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
    },

    async persistProgress(silent = true) {
      try {
        await this.persistProgressAction();
        this.lastSavedState = JSON.stringify(this.$store.state.aiOnboarding);
        if (!silent) {
          useAlert(this.$t('GENERAL.SAVED') || 'Đã lưu');
        }
      } catch (error) {
        console.error('Failed to save progress:', error);
        if (!silent) {
          useAlert(this.$t('AI_ONBOARDING.ERRORS.SAVE_FAILED') || 'Không thể lưu tiến trình');
        }
      }
    },
  },
};
</script>
